import { useEffect, useMemo, useState } from "react"
import { Link } from "react-router-dom"
import { type Stock } from "@/types/stock"
import { type Position, type DividendEvent } from "@/types/portfolio"
import { calculatePortfolioStats } from "@/lib/portfolio-math"
import { stockToPosition } from "@/lib/portfolio-utils"
import { calculateIncomeSummary } from "@/lib/forecast-helper"
import { useCurrency } from "@/context/currency/currency-context"
import { cn } from "@/lib/utils"

import { Loader2 } from "lucide-react"
import { DashboardHeader } from "@/components/custom/dashboard/dashboard-header"
import { AddPositionCard } from "@/components/custom/dashboard/add-position-card"
import { PositionsTable } from "@/components/custom/dashboard/positions-table"
import { PortfolioPieChart } from "@/components/custom/dashboard/portfolio-pie-chart"
import { PortfolioSummary } from "@/components/custom/dashboard/portfolio-summary"
import { DividendBarChart } from "@/components/custom/dashboard/dividend-bar-chart"
import { SavePortfolioDialog } from "@/components/custom/dashboard/save-portfolio-dialog"
import { PerformanceSection } from "@/components/custom/dashboard/performance-section"
import { Button } from "@/components/ui/button"
import hive from "@/lib/api/hive.ts"
import { Analytics } from "@/lib/analytics"

const STORAGE_KEY = "beediv-demo-positions"

interface SavedPosition {
	ticker: string
	exchange: string
	shares: number
}

const TICKER_EXCHANGE_MAP: Record<string, string> = {
	AAPL: "NASDAQ",
	MSFT: "NASDAQ",
	GOOGL: "NASDAQ",
	AMZN: "NASDAQ",
	META: "NASDAQ",
	TSLA: "NASDAQ",
	PEP: "NASDAQ",
	COST: "NASDAQ",
	KO: "NYSE",
	JNJ: "NYSE",
	V: "NYSE",
	PG: "NYSE",
	MCD: "NYSE",
	DIS: "NYSE",
	T: "NYSE",
}

function computeDemoForecastEvents(positions: Position[]): DividendEvent[] {
	const events: DividendEvent[] = []
	const now = new Date()
	const currentYear = now.getFullYear()
	const currentMonth = now.getMonth()

	for (const pos of positions) {
		if (!pos.dividendPerShare || pos.dividendPerShare <= 0) continue
		if (!pos.shares || pos.shares <= 0) continue

		const frequency = pos.frequency ?? "Quarterly"
		let paymentMonths: number[]
		let paymentsPerYear: number

		if (frequency === "Monthly") {
			paymentMonths = [0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11]
			paymentsPerYear = 12
		} else if (frequency === "Annual") {
			paymentMonths = [5] // June
			paymentsPerYear = 1
		} else {
			paymentMonths = [2, 5, 8, 11] // Mar, Jun, Sep, Dec
			paymentsPerYear = 4
		}

		const perPaymentAmount = (pos.dividendPerShare * pos.shares) / paymentsPerYear

		for (const year of [currentYear, currentYear + 1]) {
			for (const month of paymentMonths) {
				const payDate = new Date(year, month, 15)
				const isPast = year < currentYear || (year === currentYear && month < currentMonth)

				events.push({
					ticker: pos.ticker,
					exchange: pos.exchange,
					amount: perPaymentAmount,
					payment_date: payDate.toISOString().split("T")[0],
					ex_date: payDate.toISOString().split("T")[0],
					declaration_date: payDate.toISOString().split("T")[0],
					is_actual: isPast,
					status: isPast ? "paid" : "predicted",
					currency: pos.currency ?? "USD",
				})
			}
		}
	}

	return events
}

export default function DemoPage() {
	const { format, convert } = useCurrency()
	const [positions, setPositions] = useState<Position[]>([])
	const [isLoading, setIsLoading] = useState(false)
	const [error, setError] = useState<string | null>(null)

	// 1. Smart Hydration
	useEffect(() => {
		const hydrate = async () => {
			if (typeof window === "undefined") return
			try {
				const stored = localStorage.getItem(STORAGE_KEY)
				if (stored) {
					const savedItems: SavedPosition[] = JSON.parse(stored)
					setIsLoading(true)

					const bulkItems = savedItems.map((item) => ({
						symbol: item.ticker,
						exchange: item.exchange || TICKER_EXCHANGE_MAP[item.ticker],
					}))
					const stocks = await hive.getBulkStocks(bulkItems)

					const hydratedPositions: Position[] = stocks.map((stock) => {
						const item = savedItems.find((si) => si.ticker === stock.ticker)
						return {
							...stock,
							id: crypto.randomUUID(),
							shares: item?.shares || 0,
						}
					})
					setPositions(hydratedPositions)
				}
			} catch (error) {
				console.error("Failed to parse demo positions:", error)
				localStorage.removeItem(STORAGE_KEY)
			} finally {
				setIsLoading(false)
			}
		}
		hydrate()
	}, [])

	// 2. Optimized Persistence Effect
	useEffect(() => {
		const dataToSave: SavedPosition[] = positions.map((p) => ({
			ticker: p.ticker,
			exchange: p.exchange,
			shares: p.shares,
		}))
		localStorage.setItem(STORAGE_KEY, JSON.stringify(dataToSave))
	}, [positions])

	const handleAddPosition = (stock: Stock, shares: number) => {
		setPositions((prev) => {
			const existing = prev.find((p) => p.ticker === stock.ticker)
			if (existing) {
				return prev.map((p) => (p.ticker === stock.ticker ? { ...p, shares: p.shares + shares } : p))
			}
			return [...prev, { ...stock, shares, id: crypto.randomUUID() }]
		})
		Analytics.capture("Demo Position Added", { ticker: stock.ticker })
	}

	const handleReset = () => {
		setPositions([])
		Analytics.capture("Demo Reset")
	}

	const handleRandomize = async () => {
		const TARGET_VALUE = 100000
		const TARGET_COUNT = 8
		const COMMON_TICKERS = [
			"AAPL",
			"MSFT",
			"GOOGL",
			"AMZN",
			"META",
			"TSLA",
			"KO",
			"JNJ",
			"V",
			"PG",
			"PEP",
			"COST",
			"MCD",
			"DIS",
			"T",
		]

		const selectedTickers = [...COMMON_TICKERS].sort(() => 0.5 - Math.random()).slice(0, TARGET_COUNT)

		setIsLoading(true)
		setError(null)
		try {
			const stocks = await hive.getBulkStocks(
				selectedTickers.map((t) => ({ symbol: t, exchange: TICKER_EXCHANGE_MAP[t] })),
			)

			if (stocks.length === 0) {
				setError("Could not fetch stock data. Please try again.")
				return
			}

			const validStocks = stocks.filter((s) => s.price > 0)
			if (validStocks.length === 0) {
				setError("All fetched stocks had invalid prices. Please try again.")
				return
			}

			const randomWeights = Array.from({ length: validStocks.length }, () => Math.random())
			const weightSum = randomWeights.reduce((a, b) => a + b, 0)

			const newPositions = validStocks
				.map((stock, index) => {
					const weight = randomWeights[index] / weightSum
					const allocatedValue = TARGET_VALUE * weight
					const shares = Math.floor(allocatedValue / stock.price)
					if (shares > 0) {
						return stockToPosition(stock, shares)
					}
					return null
				})
				.filter((p): p is Position => p !== null)

			setPositions(newPositions)
			Analytics.capture("Demo Randomized")
		} catch (err) {
			console.error("Randomize failed", err)
			setError(err instanceof Error ? err.message : "Failed to generate random portfolio. Please try again.")
		} finally {
			setIsLoading(false)
		}
	}

	const filteredPositions = positions.filter((p) => (p.shares || 0) > 0)
	const stats = calculatePortfolioStats(filteredPositions)
	const { totalValue, annualIncome, portfolioYield, avgDividendGrowth, yieldOnCost } = stats

	const demoForecastEvents = useMemo(() => computeDemoForecastEvents(filteredPositions), [filteredPositions])

	const incomeSummary = useMemo(
		() => calculateIncomeSummary(demoForecastEvents, convert),
		[demoForecastEvents, convert],
	)

	if (isLoading && positions.length === 0) {
		return (
			<div className="flex flex-col items-center justify-center min-h-[60vh] gap-4">
				<Loader2 className="h-8 w-8 animate-spin text-sky-600" />
				<p className="text-muted-foreground animate-pulse">Gathering nectar from your hives...</p>
			</div>
		)
	}

	return (
		<div className="w-full max-w-[1400px] mx-auto px-4 py-8">
			<div className="lg:flex lg:gap-8">
				<div className="flex-1 min-w-0 space-y-8">
					{/* Signup nudge */}
					<div className="rounded-xl border border-sky-200 dark:border-sky-800 bg-sky-50/80 dark:bg-sky-900/20 p-4 flex items-center justify-between gap-4">
						<p className="text-sm text-sky-900 dark:text-sky-100">
							<span className="font-semibold">This is a demo sandbox.</span> Sign up to save your
							portfolio and track real dividends.
						</p>
						<Link to="/signup">
							<Button size="sm" className="shrink-0">
								Sign Up Free
							</Button>
						</Link>
					</div>

					<DashboardHeader totalValue={totalValue}>
						{positions.length > 0 && <SavePortfolioDialog positions={positions} />}
					</DashboardHeader>

					{/* Mobile only: PieChart */}
					<div className="lg:hidden h-[300px]">
						<PortfolioPieChart positions={filteredPositions} />
					</div>

					<div className="grid grid-cols-1 lg:grid-cols-3 gap-4 lg:gap-8">
						<div className="lg:col-span-2 space-y-8">
							<AddPositionCard
								onAddPosition={handleAddPosition}
								onRandomize={handleRandomize}
								onReset={handleReset}
							/>
							{error && (
								<div className="rounded-lg border border-red-200 dark:border-red-900/50 bg-red-50 dark:bg-red-950/20 p-4 text-sm text-red-700 dark:text-red-400">
									{error}
								</div>
							)}
						</div>

						<div className="lg:col-span-1">
							<div className="hidden lg:block h-[400px]">
								<PortfolioPieChart positions={filteredPositions} />
							</div>
						</div>
					</div>

					<PositionsTable positions={positions} />

					<div className="border-t border-sky-900/10 dark:border-sky-100/10 pt-8">
						<PerformanceSection demo={true} />
					</div>

					<div className="space-y-3">
						<DividendBarChart portfolioIds={[]} events={demoForecastEvents} />

						{demoForecastEvents.length > 0 && (
							<div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
								<div className="p-3 bg-white/60 dark:bg-black/20 backdrop-blur-md rounded-xl border border-sky-900/10 dark:border-sky-100/10">
									<p className="text-xs font-bold text-muted-foreground uppercase tracking-wider">
										This Month
									</p>
									<p className="text-lg font-bold text-sky-900 dark:text-sky-100 mt-1">
										{format(incomeSummary.thisMonth, { skipConversion: true })}
									</p>
								</div>
								<div className="p-3 bg-white/60 dark:bg-black/20 backdrop-blur-md rounded-xl border border-sky-900/10 dark:border-sky-100/10">
									<p className="text-xs font-bold text-muted-foreground uppercase tracking-wider">
										Year-to-Date
									</p>
									<p className="text-lg font-bold text-sky-900 dark:text-sky-100 mt-1">
										{format(incomeSummary.yearToDate, { skipConversion: true })}
									</p>
								</div>
								<div className="p-3 bg-white/60 dark:bg-black/20 backdrop-blur-md rounded-xl border border-sky-900/10 dark:border-sky-100/10">
									<p className="text-xs font-bold text-muted-foreground uppercase tracking-wider">
										Last Year
									</p>
									<p className="text-lg font-bold text-sky-900 dark:text-sky-100 mt-1">
										{format(incomeSummary.lastYearTotal, { skipConversion: true })}
									</p>
								</div>
								<div className="p-3 bg-white/60 dark:bg-black/20 backdrop-blur-md rounded-xl border border-sky-900/10 dark:border-sky-100/10">
									<p className="text-xs font-bold text-muted-foreground uppercase tracking-wider">
										YoY Growth
									</p>
									<p
										className={cn(
											"text-lg font-bold mt-1",
											incomeSummary.yoyGrowth != null && incomeSummary.yoyGrowth > 0
												? "text-green-600 dark:text-green-400"
												: incomeSummary.yoyGrowth != null && incomeSummary.yoyGrowth < 0
													? "text-red-600 dark:text-red-400"
													: "text-muted-foreground",
										)}
									>
										{incomeSummary.yoyGrowth != null
											? `${incomeSummary.yoyGrowth > 0 ? "+" : ""}${incomeSummary.yoyGrowth.toFixed(1)}%`
											: "\u2014"}
									</p>
								</div>
							</div>
						)}
					</div>
				</div>

				{/* Desktop sidebar */}
				<aside className="hidden lg:block w-80 shrink-0">
					<div className="sticky top-20 space-y-4 pb-4">
						<PortfolioSummary
							totalValue={totalValue}
							annualIncome={annualIncome}
							portfolioYield={portfolioYield}
							avgDividendGrowth={avgDividendGrowth}
							yieldOnCost={yieldOnCost}
						/>
					</div>
				</aside>
			</div>
		</div>
	)
}
